import React, {useCallback, useEffect, useRef, useState} from "react";
import {useAuth} from "../../context/user";
import {usePermissions} from "../../context/modulePermissions";
import {useScopeQueryParams} from "./hooks/useScopeQueryParams";
import {useSiren} from "../../pages/siren/SirenContext";
import {Layout} from "../../pages/siren/Layout";
import CodeMirror from "@uiw/react-codemirror";
import {css} from "@codemirror/lang-css";
import {BlueButton} from "../../components/ui/Buttons";
import {SirenEmailBranding} from "./hooks/useGetSirenEmailBrandings";
import {useUpsertSirenEmailBranding} from "./hooks/useUpsertSirenEmailBranding";
import {useDeleteSirenEmailBranding} from "./hooks/useDeleteSirenEmailBranding";
import {platformAPI, platformFormAPI} from "../../api/axiosConfig";
import DeleteIcon from "@mui/icons-material/Delete";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import {ScopeSplitSelector, ScopeSelection} from "./components/ScopeSplitSelector";

type FormState = {
    theme: string;
    customCSS: string;
    footerText: string;
    sirenRecipientPortalURL: string;
};

const emptyForm = (): FormState => ({theme: "", customCSS: "", footerText: "", sirenRecipientPortalURL: ""});


const LightbulbToggle: React.FC<{dark: boolean; onToggle: () => void}> = ({dark, onToggle}) => (
    <button
        type="button"
        title="Toggle dark background"
        onClick={onToggle}
        className="absolute top-3 right-3 p-0.5 rounded transition-colors"
    >
        {dark
            ? <LightbulbIcon style={{fontSize: 22}} className="text-white"/>
            : <LightbulbOutlinedIcon style={{fontSize: 22}} className="text-light-grey"/>
        }
    </button>
);

// --- Preview ---

const PreviewButton: React.FC<{clientID?: string; departmentID?: string; theme: string; customCSS: string; footerText: string}> = ({clientID, departmentID, theme, customCSS, footerText}) => {
    const [html, setHtml] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleOpen = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await platformAPI.post<string>("api/siren/email-branding/preview", {clientID, departmentID, theme, customCSS, footerText}, {responseType: "text"});
            setHtml(res.data);
        } catch {
            setError("Failed to load preview");
        } finally {
            setLoading(false);
        }
    }, [clientID, departmentID, theme, customCSS, footerText]);

    return (
        <>
            <button
                type="button"
                className="px-4 h-9 rounded-md border border-light-grey text-sm font-medium hover:bg-light-light-grey transition-colors disabled:opacity-50"
                onClick={handleOpen}
                disabled={loading}
            >
                {loading ? "Loading…" : "Preview Email"}
            </button>

            {(html !== null || error) && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-lg flex flex-col w-full max-w-4xl max-h-[90vh]">
                        <div className="flex items-center justify-between px-5 py-3 border-b border-light-light-grey">
                            <span className="font-semibold text-sm">Email Preview</span>
                            <button
                                className="text-light-grey hover:text-dark-grey text-lg leading-none"
                                onClick={() => { setHtml(null); setError(null); }}
                            >
                                ×
                            </button>
                        </div>
                        <div className="flex-1 overflow-hidden">
                            {error && <p className="p-4 text-sm text-red">{error}</p>}
                            {html !== null && (
                                <iframe
                                    srcDoc={html}
                                    sandbox="allow-same-origin"
                                    className="w-full h-full border-0"
                                    style={{minHeight: "70vh"}}
                                    title="Email preview"
                                />
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

// --- Send Test ---

const SendTestButton: React.FC<{clientID?: string; departmentID?: string}> = ({clientID, departmentID}) => {
    const [open, setOpen] = useState(false);
    const [toAddress, setToAddress] = useState("");
    const [sending, setSending] = useState(false);
    const [result, setResult] = useState<{ok: boolean; message: string} | null>(null);

    const handleSend = useCallback(async () => {
        setSending(true);
        setResult(null);
        try {
            await platformAPI.post("api/siren/email-branding/test", {clientID, departmentID, toAddress});
            setResult({ok: true, message: "Test email sent successfully."});
        } catch (e: any) {
            const msg = e?.response?.data?.error ?? "Failed to send test email.";
            setResult({ok: false, message: msg});
        } finally {
            setSending(false);
        }
    }, [clientID, departmentID, toAddress]);

    const handleClose = () => { setOpen(false); setToAddress(""); setResult(null); };

    return (
        <>
            <button
                type="button"
                className="px-4 h-9 rounded-md border border-light-grey text-sm font-medium hover:bg-light-light-grey transition-colors"
                onClick={() => setOpen(true)}
            >
                Send Test Email
            </button>

            {open && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-lg flex flex-col w-full max-w-md">
                        <div className="flex items-center justify-between px-5 py-3 border-b border-light-light-grey">
                            <span className="font-semibold text-sm">Send Test Email</span>
                            <button className="text-light-grey hover:text-dark-grey text-lg leading-none" onClick={handleClose}>×</button>
                        </div>
                        <div className="p-5 flex flex-col gap-4">
                            <div className="flex flex-col gap-1">
                                <label className="text-sm font-medium">Recipient Email</label>
                                <input
                                    type="email"
                                    className="border border-light-light-grey rounded-md px-3 h-9 text-sm focus:outline-none focus:border-blue"
                                    placeholder="you@example.com"
                                    value={toAddress}
                                    onChange={e => setToAddress(e.target.value)}
                                />
                            </div>
                            {result && (
                                <p className={`text-sm ${result.ok ? "text-green" : "text-red"}`}>{result.message}</p>
                            )}
                        </div>
                        <div className="flex justify-end gap-2 px-5 pb-5">
                            <button
                                type="button"
                                className="px-4 h-9 rounded-md border border-light-grey text-sm font-medium hover:bg-light-light-grey transition-colors"
                                onClick={handleClose}
                            >
                                Close
                            </button>
                            <div className="w-36">
                                <BlueButton text="Send" onClick={handleSend} loading={sending} disabled={!toAddress || sending}/>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

// --- Inline email branding panel ---

type EmailBrandingPanelProps = {
    scope: ScopeSelection;
    onDeleted: () => void;
};

const EmailBrandingPanel: React.FC<EmailBrandingPanelProps> = ({scope, onDeleted}) => {
    const {save, saving, error: saveError} = useUpsertSirenEmailBranding();
    const {deleteBranding, deleting} = useDeleteSirenEmailBranding();
    const {hasFullAccess} = usePermissions();

    const [record, setRecord] = useState<SirenEmailBranding | null>(null);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState<FormState>(() => emptyForm());
    const [saved, setSaved] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState(false);

    const [logoUploading, setLogoUploading] = useState(false);
    const [logoProgress, setLogoProgress] = useState(0);
    const [logoProcessing, setLogoProcessing] = useState(false);
    const [logoRemoving, setLogoRemoving] = useState(false);
    const [logoRemoveConfirm, setLogoRemoveConfirm] = useState(false);
    const [logoDarkBg, setLogoDarkBg] = useState(false);
    const [logoPreviewFile, setLogoPreviewFile] = useState<File | null>(null);
    const [logoPreviewURL, setLogoPreviewURL] = useState<string | null>(null);
    const [logoPreviewDark, setLogoPreviewDark] = useState(false);
    const [logoError, setLogoError] = useState<string | null>(null);
    const [logoObjectURL, setLogoObjectURL] = useState<string | null>(null);
    const [newLogoObjectURL, setNewLogoObjectURL] = useState<string | null>(null);
    const logoInputRef = useRef<HTMLInputElement>(null);
    const logoPollRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        setLoading(true);
        setSaved(false);
        setRecord(null);
        const params: Record<string, string> = {};
        if (scope.client) params.clientID = scope.client.id;
        if (scope.department) params.departmentID = scope.department.id;
        platformAPI.get<SirenEmailBranding>("api/siren/email-branding/exact", {params})
            .then(res => {
                setRecord(res.data);
                setForm({
                    theme: res.data.theme ?? "",
                    customCSS: res.data.customCSS ?? "",
                    footerText: res.data.footerText ?? "",
                    sirenRecipientPortalURL: res.data.sirenRecipientPortalURL ?? "",
                });
            })
            .catch(() => {
                setForm(emptyForm());
            })
            .finally(() => setLoading(false));
    }, [scope.client?.id, scope.department?.id]);

    useEffect(() => {
        if (!record?.id || record.logoStatus !== "ready") {
            setLogoObjectURL(null);
            return;
        }
        let objectURL: string | null = null;
        platformAPI.get<Blob>(`api/siren/email-branding/${record.id}/logo`, {responseType: "blob"})
            .then(res => {
                objectURL = URL.createObjectURL(res.data);
                setLogoObjectURL(objectURL);
            })
            .catch(() => setLogoObjectURL(null));
        return () => { if (objectURL) URL.revokeObjectURL(objectURL); };
    }, [record?.id, record?.logoStatus]);

    useEffect(() => () => { if (logoPollRef.current) clearTimeout(logoPollRef.current); }, []);
    useEffect(() => () => { if (logoPreviewURL) URL.revokeObjectURL(logoPreviewURL); }, [logoPreviewURL]);

    const handleSave = useCallback(async () => {
        setSaved(false);
        try {
            const result = await save({
                clientID: scope.client?.id,
                departmentID: scope.department?.id,
                theme: form.theme,
                customCSS: form.customCSS,
                footerText: form.footerText,
                sirenRecipientPortalURL: form.sirenRecipientPortalURL,
            }, record?.id);
            setRecord(result);
            setNewLogoObjectURL(null);
            setSaved(true);
        } catch {
            // error shown via saveError
        }
    }, [form, record, save, scope]);

    const handleLogoUpload = useCallback(async (file: File) => {
        if (!record?.id) return;
        setLogoError(null);
        setLogoProgress(0);
        setLogoUploading(true);
        setLogoProcessing(false);
        setNewLogoObjectURL(null);
        if (logoPollRef.current) clearTimeout(logoPollRef.current);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const res = await platformFormAPI.post<SirenEmailBranding>(`api/siren/email-branding/${record.id}/logo`, fd, {
                onUploadProgress: (e) => {
                    if (e.total) setLogoProgress(Math.round((e.loaded / e.total) * 100));
                },
            });
            setRecord(res.data);
            setLogoProcessing(true);
            const brandingId = res.data.id;
            const poll = () => {
                logoPollRef.current = setTimeout(async () => {
                    try {
                        const check = await platformAPI.get<SirenEmailBranding>(`api/siren/email-branding/${brandingId}`);
                        if (check.data.logoStatus === "ready") {
                            setLogoProcessing(false);
                            const img = await platformAPI.get<Blob>(`api/siren/email-branding/${brandingId}/logo`, {responseType: "blob"});
                            setNewLogoObjectURL(URL.createObjectURL(img.data));
                        } else {
                            poll();
                        }
                    } catch {
                        setLogoProcessing(false);
                    }
                }, 2000);
            };
            poll();
        } catch (e: any) {
            const fieldError = e?.response?.data?.errors?.file?.[0] ?? e?.response?.data?.error;
            setLogoError(fieldError ?? "error uploading logo");
        } finally {
            setLogoUploading(false);
        }
    }, [record]);

    const handleLogoRemove = useCallback(async () => {
        if (!record?.id) return;
        setLogoRemoving(true);
        try {
            const res = await platformAPI.delete<SirenEmailBranding>(`api/siren/email-branding/${record.id}/logo`);
            setRecord(res.data);
            setLogoObjectURL(null);
            setNewLogoObjectURL(null);
            if (logoPollRef.current) clearTimeout(logoPollRef.current);
            setLogoProcessing(false);
        } catch {
            // silently ignore
        } finally {
            setLogoRemoving(false);
        }
    }, [record]);

    const handleDelete = useCallback(async () => {
        if (!record) return;
        setDeleteConfirm(false);
        try {
            await deleteBranding(record.id, {clientID: scope.client?.id, departmentID: scope.department?.id});
            setRecord(null);
            setForm(emptyForm());
            onDeleted();
        } catch {
            // silently ignore
        }
    }, [record, deleteBranding, scope, onDeleted]);

    if (loading) return <div className="p-4 text-sm text-light-grey">Loading…</div>;

    return (
        <div className={`flex flex-col gap-4 p-5 overflow-y-auto${!hasFullAccess("siren_config") ? " pointer-events-none" : ""}`}>
            <div>
                <p className="font-semibold text-sm">{scope.client ? scope.client.name : "System Default"}</p>
                <p className="text-xs text-light-grey">{scope.department ? scope.department.name : scope.client ? "Client level" : "Applies to all clients without their own branding"}</p>
            </div>

            {/* Options */}
            <div className="bg-white shadow-md rounded-lg p-4 flex flex-col gap-3">
                <div className="flex flex-col gap-1">
                    <label className="font-medium text-sm">Theme</label>
                    <input
                        className="h-9 shadow-md rounded-md px-3 outline-none bg-white text-sm"
                        placeholder="leave blank for default"
                        value={form.theme}
                        onChange={e => setForm(f => ({...f, theme: e.target.value}))}
                    />
                </div>
                <div className="flex flex-col gap-1">
                    <label className="font-medium text-sm">Footer Text <span className="text-light-grey font-normal">(optional)</span></label>
                    <input
                        className="h-9 shadow-md rounded-md px-3 outline-none bg-white text-sm"
                        placeholder="© Northcott Global Solutions. All rights reserved."
                        value={form.footerText}
                        onChange={e => setForm(f => ({...f, footerText: e.target.value}))}
                    />
                </div>
                <div className="flex flex-col gap-1">
                    <label className="font-medium text-sm">Recipient Portal URL <span className="text-light-grey font-normal">(optional)</span></label>
                    <input
                        className="h-9 shadow-md rounded-md px-3 outline-none bg-white text-sm"
                        placeholder="https://portal.example.com"
                        value={form.sirenRecipientPortalURL}
                        onChange={e => setForm(f => ({...f, sirenRecipientPortalURL: e.target.value}))}
                    />
                </div>
            </div>

            {/* Logo */}
            <div className="bg-white shadow-md rounded-lg p-4 flex flex-col gap-2">
                <label className="font-medium text-sm">Logo</label>
                {!record?.id ? (
                    <p className="text-sm text-light-grey">Save first to enable logo upload.</p>
                ) : (
                    <>
                        {logoObjectURL && (
                            <div className={`relative flex items-center justify-center rounded-md border border-light-light-grey h-24 transition-colors ${logoDarkBg ? "bg-dark-dark-grey" : "bg-light-light-grey bg-opacity-30"}`}>
                                <LightbulbToggle dark={logoDarkBg} onToggle={() => setLogoDarkBg(v => !v)}/>
                                <img src={logoObjectURL} alt="Brand logo" className="max-h-full max-w-full object-contain p-2"/>
                            </div>
                        )}
                        {logoUploading && (
                            <div className="w-full bg-light-light-grey rounded-full h-2 overflow-hidden">
                                <div className="bg-blue h-2 rounded-full transition-all duration-200" style={{width: `${logoProgress}%`}}/>
                            </div>
                        )}
                        {logoProcessing && <p className="text-sm text-light-grey">Processing…</p>}
                        {!logoUploading && newLogoObjectURL && (
                            <div className={`relative flex items-center justify-center rounded-md border border-light-light-grey h-24 transition-colors ${logoDarkBg ? "bg-dark-dark-grey" : "bg-light-light-grey bg-opacity-30"}`}>
                                <LightbulbToggle dark={logoDarkBg} onToggle={() => setLogoDarkBg(v => !v)}/>
                                <img src={newLogoObjectURL} alt="New brand logo" className="max-h-full max-w-full object-contain p-2"/>
                            </div>
                        )}
                        {!logoObjectURL && !logoUploading && !logoProcessing && !newLogoObjectURL && (
                            <p className="text-sm text-light-grey">No logo uploaded</p>
                        )}
                        <input
                            ref={logoInputRef}
                            type="file"
                            accept="image/png,image/jpeg,image/webp"
                            className="hidden"
                            onChange={e => {
                                const file = e.target.files?.[0];
                                if (file) {
                                    setLogoPreviewFile(file);
                                    setLogoPreviewURL(URL.createObjectURL(file));
                                    setLogoPreviewDark(false);
                                }
                                e.target.value = "";
                            }}
                        />
                        <div className="flex gap-2 flex-wrap">
                            <button
                                type="button"
                                className="px-4 h-9 rounded-md bg-blue text-white text-sm font-medium hover:bg-opacity-90 transition-colors disabled:opacity-50"
                                disabled={logoUploading || logoRemoving}
                                onClick={() => logoInputRef.current?.click()}
                            >
                                {record.logoStatus ? "Replace Logo" : "Upload Logo"}
                            </button>
                            {record.logoStatus && (
                                <button
                                    type="button"
                                    className="flex items-center gap-1.5 px-3 h-9 rounded-md border border-red text-red text-sm font-medium hover:bg-red hover:text-white transition-colors disabled:opacity-50"
                                    disabled={logoUploading || logoRemoving}
                                    onClick={() => setLogoRemoveConfirm(true)}
                                >
                                    <DeleteIcon style={{fontSize: 16}}/>
                                    Remove
                                </button>
                            )}
                        </div>
                        {logoError && <p className="text-sm text-red">{logoError}</p>}
                    </>
                )}
            </div>

            {/* Custom CSS */}
            <div className="bg-white shadow-md rounded-lg p-4 flex flex-col gap-2">
                <label className="font-medium text-sm">Custom CSS</label>
                <div className="rounded-md overflow-hidden border border-light-light-grey max-h-64 overflow-y-auto">
                    <CodeMirror
                        value={form.customCSS}
                        extensions={[css()]}
                        onChange={v => setForm(f => ({...f, customCSS: v}))}
                        placeholder="/* custom email styles */"
                        minHeight="120px"
                        basicSetup={{lineNumbers: true, foldGutter: false}}
                    />
                </div>
            </div>

            {saveError && <div className="bg-red text-white text-sm text-center py-2 px-4 rounded-md">{saveError}</div>}
            {saved && <div className="bg-green text-white text-sm text-center py-2 px-4 rounded-md">Saved</div>}

            <div className="flex justify-end gap-2 items-center flex-wrap">
                {record?.id && (
                    <>
                        <SendTestButton clientID={scope.client?.id} departmentID={scope.department?.id}/>
                        <PreviewButton clientID={scope.client?.id} departmentID={scope.department?.id} theme={form.theme} customCSS={form.customCSS} footerText={form.footerText}/>
                    </>
                )}
                {record && hasFullAccess("siren_config") && (
                    <button
                        type="button"
                        className="h-10 px-3 rounded-md bg-red text-white hover:bg-opacity-80 transition-colors flex items-center gap-1.5 text-sm font-medium"
                        onClick={() => setDeleteConfirm(true)}
                    >
                        <DeleteIcon style={{fontSize: 16}}/>
                        Delete
                    </button>
                )}
                {hasFullAccess("siren_config") && (
                    <div className="w-32">
                        <BlueButton text="Save" onClick={handleSave} loading={saving} disabled={logoUploading || logoRemoving || saving}/>
                    </div>
                )}
            </div>

            {deleteConfirm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-lg p-6 flex flex-col gap-4 max-w-sm w-full">
                        <h3 className="font-bold">Delete Email Branding</h3>
                        <p className="text-sm">Delete email branding for <span className="font-semibold">{scope.client ? scope.client.name : "System Default"}</span>{scope.department ? <> / <span className="font-semibold">{scope.department.name}</span></> : ""}?</p>
                        <div className="flex gap-2 justify-end">
                            <button className="px-4 py-2 text-sm rounded-md border border-light-grey hover:bg-light-light-grey" onClick={() => setDeleteConfirm(false)}>Cancel</button>
                            <button className="px-4 py-2 text-sm rounded-md bg-red text-white hover:bg-opacity-80 disabled:opacity-50" disabled={deleting} onClick={handleDelete}>Delete</button>
                        </div>
                    </div>
                </div>
            )}

            {logoRemoveConfirm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-lg p-6 flex flex-col gap-4 max-w-sm w-full">
                        <h3 className="font-bold">Remove Logo</h3>
                        <p className="text-sm">Are you sure you want to remove the logo?</p>
                        <div className="flex gap-2 justify-end">
                            <button className="px-4 py-2 text-sm rounded-md border border-light-grey hover:bg-light-light-grey" onClick={() => setLogoRemoveConfirm(false)}>Cancel</button>
                            <button className="px-4 py-2 text-sm rounded-md bg-red text-white hover:bg-opacity-80 disabled:opacity-50" disabled={logoRemoving} onClick={() => { setLogoRemoveConfirm(false); handleLogoRemove(); }}>Remove</button>
                        </div>
                    </div>
                </div>
            )}

            {logoPreviewURL && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-lg p-6 flex flex-col gap-4 w-full max-w-lg">
                        <h3 className="font-bold">Preview Logo</h3>
                        <div className={`relative flex items-center justify-center rounded-md border border-light-light-grey h-48 transition-colors ${logoPreviewDark ? "bg-dark-dark-grey" : "bg-light-light-grey bg-opacity-30"}`}>
                            <LightbulbToggle dark={logoPreviewDark} onToggle={() => setLogoPreviewDark(v => !v)}/>
                            <img src={logoPreviewURL} alt="Logo preview" className="max-h-full max-w-full object-contain p-2"/>
                        </div>
                        <div className="flex gap-2 justify-end">
                            <button className="px-4 py-2 text-sm rounded-md border border-light-grey hover:bg-light-light-grey" onClick={() => { URL.revokeObjectURL(logoPreviewURL); setLogoPreviewURL(null); setLogoPreviewFile(null); }}>Cancel</button>
                            <button className="px-4 py-2 text-sm rounded-md bg-blue text-white" onClick={() => { URL.revokeObjectURL(logoPreviewURL); setLogoPreviewURL(null); if (logoPreviewFile) handleLogoUpload(logoPreviewFile); setLogoPreviewFile(null); }}>Upload</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// --- List / Choose page ---

export const SirenEmailBrandingPage: React.FC = () => {
    const {clientID} = useSiren();
    const {isSuperAdmin, isDepartmentAdmin} = useAuth();
    const {scope: selectedScope, drillClientID, drillClientName, deptPage, onSelect, onDrill, onDeptPageChange, onClear} = useScopeQueryParams({defaultClientID: isSuperAdmin ? null : clientID});

    return (
        <Layout menuCurrent="config">
            <div className="flex gap-4 h-full">
                <div className="w-[460px] shrink-0">
                    <ScopeSplitSelector
                        selectedScope={selectedScope}
                        onSelect={onSelect}
                        onClear={onClear}
                        onDrill={onDrill}
                        initialClientID={drillClientID}
                        initialClientName={drillClientName}
                        deptPage={deptPage}
                        onDeptPageChange={onDeptPageChange}
                        hydrate="emailBrandingExistence"
                        showSystemDefault={isSuperAdmin}
                        showClientLevel={!isDepartmentAdmin}
                    />
                </div>
                <div className="flex-1 min-w-0 flex flex-col">
                    {!selectedScope && (
                        <div className="p-5 text-sm text-light-grey">
                            Select a client, then a scope to configure.
                        </div>
                    )}
                    {selectedScope && (
                        <EmailBrandingPanel
                            key={`${selectedScope.client?.id ?? "system"}-${selectedScope.department?.id ?? "client"}`}
                            scope={selectedScope}
                            onDeleted={() => {}}
                        />
                    )}
                </div>
            </div>
        </Layout>
    );
};

export default SirenEmailBrandingPage;
